import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { chargingApi } from '@/api/charging';
import { pointsApi } from '@/api/points';
import { walletApi, walletNumbers } from '@/api/wallet';
import { friendlyMessage } from '@/lib/errors';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';

/**
 * Gateway autenticado do fluxo QR (`/qr/:stationId/:connectorId`).
 *
 * O cliente que escaneia o QR já escolheu o conector na landing — não faz
 * sentido fazê-lo confirmar de novo no ConfigureCharge. Aqui:
 *
 *   sessão ativa     → /charging/:active.id
 *   tudo OK          → chargingApi.start → /charging/:session.id
 *   saldo insuficiente / tarifa inválida / cabo em uso → /configure/... (manual)
 *   falha no start   → /configure/... com `state.autoStartError` (banner pronto)
 *
 * Outros pontos de entrada (PointCard/StationDetail/StationCard) continuam
 * indo direto pra /configure/... — esses NÃO passam por aqui.
 */
export default function QRHandler() {
  const { stationId, connectorId: connectorNumber } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'resolving' | 'starting'>('resolving');

  useEffect(() => {
    if (!stationId || !connectorNumber) {
      setError('QR inválido');
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        // 1. Resolve stationId + connectorNumber → point + connector
        const points = await pointsApi.list();
        const match = points.find((p) => p.hub_station_id === stationId);
        if (!match) {
          if (!cancelled) setError(`Ponto ${stationId} não cadastrado`);
          return;
        }
        const [detail, wallet, active] = await Promise.all([
          pointsApi.get(match.id),
          walletApi.get(),
          chargingApi.active()
        ]);
        if (cancelled) return;

        // Já tem sessão ativa — vai direto pra ela (replace pra evitar volta
        // pro QR num back).
        if (active) {
          navigate(`/charging/${active.id}`, { replace: true });
          return;
        }

        const connector = detail.connectors.find(
          (c) => c.connector_number === Number(connectorNumber)
        );
        if (!connector) {
          if (!cancelled) setError(`Conector ${connectorNumber} não encontrado`);
          return;
        }

        const configurePath = `/configure/${match.id}/${connector.id}`;

        // Preconditions pro auto-start. Qualquer um falso → cai no fluxo
        // manual (ConfigureCharge já tem as telas certas: saldo insuficiente,
        // tarifa indisponível, etc.).
        // O auto-start não escolhe carteira — vai no default do backend, que
        // é SALDO. Por isso o gate aqui é o mínimo do saldo (R$ 25 desde a
        // migration 050), não o operacional. Quem não alcança cai no
        // ConfigureCharge, que é onde dá pra escolher pagar com cashback.
        const { available, minBalance } = walletNumbers(wallet);
        const tariff = Number(detail.tariff_per_kwh_brl ?? 0);
        const canStart =
          available >= minBalance &&
          tariff > 0 &&
          isChargerSelectable(connector.state);

        if (!canStart) {
          // viaQr: veio do QR físico — sem a trava de "eletroposto errado".
          navigate(configurePath, { replace: true, state: { viaQr: true } });
          return;
        }

        // 2. Auto-start
        setPhase('starting');
        try {
          const session = await chargingApi.start({
            point_id: match.id,
            connector_id: connector.id,
            limit_type: 'full_balance',
            limit_value: '0'
          });
          if (cancelled) return;
          navigate(`/charging/${session.id}`, { replace: true });
        } catch (startErr) {
          if (cancelled) return;
          // Repassa a mensagem amigável (ex.: "Esta estação já tem outro cabo
          // em uso…") pra ConfigureCharge mostrar no banner — sem precisar o
          // usuário re-tentar e ver de novo.
          navigate(configurePath, {
            replace: true,
            state: { autoStartError: friendlyMessage(startErr), viaQr: true }
          });
        }
      } catch (err) {
        if (!cancelled) setError(friendlyMessage(err, 'Falha ao resolver QR'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stationId, connectorNumber, navigate]);

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-black">QR não reconhecido</h1>
        <div className="rounded-2xl bg-white p-4 text-sm text-bow-red">{error}</div>
        <Link
          to="/"
          className="inline-flex h-11 items-center justify-center rounded-xl bg-bow-blue px-5 font-black text-white"
        >
          Voltar
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 p-8">
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-bow-royal/20 border-t-bow-blue" />
      <p className="text-sm text-bow-royal/70">
        {phase === 'starting' ? 'Iniciando recarga…' : 'Identificando conector…'}
      </p>
    </div>
  );
}
